import { useEffect, useRef } from "react";
import type { Notification } from "../../hooks/useNotifications";
import { useAnalytics } from "../../lib/analytics";
import { useClientTranslations } from "../../lib/i18n/client";
import { BellIcon } from "../icons/BellIcon";
import styles from "./NotificationDropdown.module.css";
import { NotificationItem } from "./NotificationItem";

interface NotificationDropdownProps {
	isOpen: boolean;
	onClose: () => void;
	notifications: Notification[];
	onMarkAsRead: (id: string) => void;
	onMarkAllAsRead: () => void;
	onActionClick?: (
		notification: Notification,
		action: "accept" | "decline",
	) => void;
}

export function NotificationDropdown({
	isOpen,
	onClose,
	notifications,
	onMarkAsRead,
	onMarkAllAsRead,
	onActionClick,
}: NotificationDropdownProps) {
	const dropdownRef = useRef<HTMLDivElement>(null);
	const { t } = useClientTranslations("common");
	const analytics = useAnalytics();

	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				onClose();
			}
		}

		function handleEscape(event: KeyboardEvent) {
			if (event.key === "Escape") {
				onClose();
			}
		}

		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside);
			document.addEventListener("keydown", handleEscape);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			document.removeEventListener("keydown", handleEscape);
		};
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	const hasUnread = notifications.some((n) => !n.isRead);

	return (
		<>
			{/* Backdrop */}
			<button
				type="button"
				className={styles.backdrop}
				onClick={onClose}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						onClose();
					}
				}}
				aria-label={t("accessibility.closeMenu") || "Close menu"}
			/>

			{/* Dropdown */}
			<div ref={dropdownRef} className={styles.dropdown}>
				{/* Header */}
				<div className={styles.header}>
					<h2 className={styles.title}>
						{t("notifications.title") || "Notifications"}
					</h2>
					{hasUnread && (
						<button
							type="button"
							onClick={() => {
								analytics.logEvent("notifications_mark_all_as_read");
								onMarkAllAsRead();
							}}
							className={styles.markAllButton}
						>
							{t("buttons.markAllAsRead") || "Mark all as read"}
						</button>
					)}
				</div>

				{/* Notifications list */}
				<div className={styles.list}>
					{notifications.length > 0 ? (
						notifications.map((notification) => (
							<NotificationItem
								key={notification.id}
								notification={notification}
								onMarkAsRead={onMarkAsRead}
								onActionClick={onActionClick}
							/>
						))
					) : (
						<div className={styles.empty}>
							<BellIcon size={48} className={styles.emptyIcon} />
							<p className={styles.emptyTitle}>
								{t("notifications.noNotifications") || "No notifications"}
							</p>
							<p className={styles.emptySubtitle}>
								{t("notifications.allCaughtUp") || "You're all caught up!"}
							</p>
						</div>
					)}
				</div>
			</div>
		</>
	);
}
